import { TimerConfig } from "../types/update.types";

/**
 * A timer class that manages countdown functionality with configurable callbacks.
 */
export class Timer {
  /** The interval ID for the timer's tick function */
  private intervalId: NodeJS.Timeout | null = null;
  /** Timestamp when the timer was started */
  private startTime: number | null = null;
  /** Configuration object containing duration and callback functions */
  private config: TimerConfig | null = null;

  /**
   * Starts the timer with the given configuration
   * @param config - Timer configuration containing duration and callbacks
   */
  public start(config: TimerConfig): void {
    this.stop();
    this.config = config;
    this.startTime = Date.now();

    this.intervalId = setInterval(() => {
      const timeLeftInMs = this.getTimeLeftInMs();
      this.config?.onTimeLeftChange?.(timeLeftInMs);

      if (timeLeftInMs <= 0) {
        this.stop();
        this.config?.onComplete?.();
      }
    }, 500);
  }

  /**
   * Stops the timer and clears all internal state
   */
  public stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      this.startTime = null;
    }
  }

  /**
   * Calculates the remaining time in milliseconds
   * @returns The number of milliseconds remaining, or 0 if timer is not running
   */
  public getTimeLeftInMs(): number {
    if (!this.startTime || !this.config) {
      return 0;
    }

    return Math.max(0, this.config.duration - (Date.now() - this.startTime));
  }

  /**
   * Checks if the timer is currently running
   * @returns True if the timer is running, false otherwise
   */
  public isRunning(): boolean {
    return this.intervalId !== null;
  }
}
